import React, { FC, useCallback, useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import PlaceIcon from "@mui/icons-material/Place";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import useAuth from "hooks/useAuth";
import { useAppDispatch, useAppSelector } from "hooks/useReduxStore";
import { UserGroupCategory, UserGroupKey } from "client/jspPlatformExperiment";
import ADMIN_USER_GROUP_ID from "models/userGroup";
import { userGroupActions } from "store/slices/userGroup/userGroupOptionsSlice";
import userGroupOptionsSelector from "store/selectors/userGroup/userGroupOptionSelector";
import {
  currentSiteActions,
  CURRENT_SITE_STORAGE_KEY,
} from "store/slices/userGroup/currentSiteSlice";
import currentSiteSelector from "store/selectors/userGroup/currentSiteSelector";

const SiteSelectBox = styled(Box)`
  display: flex;
  align-items: center;
  margin-right: 16px;
  padding: 2px 10px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.7);
`;

// Coffee-brown text/icon colors so content stays legible on the light pill.
const TEXT_COLOR = "#8b6f52";
const PLACEHOLDER_COLOR = "rgba(139, 111, 82, 0.6)";

const ColorDot = styled.span<{ $color?: string }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 8px;
  flex-shrink: 0;
  background: ${(props) => props.$color ?? "rgba(255, 255, 255, 0.6)"};
`;

interface SiteSelectorProps {}

/**
 * Navbar dropdown for choosing the site the user is working with.
 *
 * Options come from the user's assigned SITE groups; if they have none and are
 * an admin, all sites are fetched instead. The chosen site is stored globally
 * (Redux + localStorage). Renders nothing when there are no options, and a
 * static label when there is exactly one.
 */
const SiteSelector: FC<SiteSelectorProps> = () => {
  const auth = useAuth();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const userGroups = auth.user?.user_groups;

  const assignedSites = useMemo(
    () =>
      (userGroups ?? []).filter(
        (group) => group.group_category === UserGroupCategory.SITE,
      ),
    [userGroups],
  );

  const isAdmin = useMemo(
    () =>
      (userGroups ?? []).some(
        (group) =>
          group.group_category === UserGroupCategory.ROLE &&
          group.id === ADMIN_USER_GROUP_ID,
      ),
    [userGroups],
  );

  const useAdminAllSites = assignedSites.length === 0 && isAdmin;

  const siteUserGroupOptions = useAppSelector(
    userGroupOptionsSelector.siteUserGroupOptions,
  );
  const siteColorMap = useAppSelector(userGroupOptionsSelector.siteColorMap);
  const currentSiteId = useAppSelector(currentSiteSelector.currentSiteId);

  // Make sure the option metadata (names, colors) is available: assigned sites
  // are merged in directly; admins fetch the full set from the API.
  useEffect(() => {
    if (assignedSites.length > 0) {
      dispatch(userGroupActions.setUserGroupOptions(assignedSites));
    } else if (isAdmin) {
      dispatch(userGroupActions.fetchSiteUserGroupOptions());
    }
  }, [dispatch, assignedSites, isAdmin]);

  const options: UserGroupKey[] = useMemo(() => {
    if (assignedSites.length > 0) {
      return assignedSites;
    }
    if (useAdminAllSites) {
      return Object.values(siteUserGroupOptions);
    }
    return [];
  }, [assignedSites, useAdminAllSites, siteUserGroupOptions]);

  // Auto-select the only site, or clear a stale persisted selection that is no
  // longer among the user's options.
  useEffect(() => {
    if (options.length === 0) {
      return;
    }
    const ids = options.map((option) => option.id);
    if (options.length === 1) {
      if (currentSiteId !== ids[0]) {
        dispatch(currentSiteActions.setCurrentSite(ids[0]));
      }
    } else if (currentSiteId !== null && !ids.includes(currentSiteId)) {
      dispatch(currentSiteActions.setCurrentSite(null));
    }
  }, [options, currentSiteId, dispatch]);

  // Persist the selection so it survives reloads.
  useEffect(() => {
    if (currentSiteId === null) {
      localStorage.removeItem(CURRENT_SITE_STORAGE_KEY);
    } else {
      localStorage.setItem(CURRENT_SITE_STORAGE_KEY, String(currentSiteId));
    }
  }, [currentSiteId]);

  const labelFor = useCallback(
    (option?: UserGroupKey | null): string => {
      if (!option) {
        return "";
      }
      return (
        option.group_name ??
        siteUserGroupOptions[option.id]?.group_name ??
        `Site ${option.id}`
      );
    },
    [siteUserGroupOptions],
  );

  const handleChange = useCallback(
    (event: SelectChangeEvent<number | "">) => {
      const { value } = event.target;
      dispatch(
        currentSiteActions.setCurrentSite(value === "" ? null : Number(value)),
      );
    },
    [dispatch],
  );

  if (options.length === 0) {
    return null;
  }

  if (options.length === 1) {
    const only = options[0];
    return (
      <SiteSelectBox>
        <PlaceIcon sx={{ color: TEXT_COLOR, fontSize: 18, mr: 0.5 }} />
        <Typography sx={{ color: TEXT_COLOR, fontWeight: 500 }}>
          {labelFor(only)}
        </Typography>
      </SiteSelectBox>
    );
  }

  return (
    <SiteSelectBox>
      <PlaceIcon sx={{ color: TEXT_COLOR, fontSize: 18, mr: 0.5 }} />
      <Select<number | "">
        value={currentSiteId ?? ""}
        onChange={handleChange}
        displayEmpty
        variant="standard"
        renderValue={(selected) => {
          if (selected === "") {
            return (
              <Typography sx={{ color: PLACEHOLDER_COLOR }}>
                {t("Site")}
              </Typography>
            );
          }
          const option = options.find((item) => item.id === selected);
          return (
            <Typography sx={{ color: TEXT_COLOR }}>
              {labelFor(option)}
            </Typography>
          );
        }}
        sx={{
          color: TEXT_COLOR,
          minWidth: 140,
          fontWeight: 500,
          "& .MuiSvgIcon-root": { color: TEXT_COLOR },
          "& .MuiSelect-select": { py: 0.25, backgroundColor: "transparent" },
          "& .MuiSelect-select:focus": { backgroundColor: "transparent" },
          "&:before, &:after": { display: "none" },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            <ColorDot $color={siteColorMap[option.id]} />
            {labelFor(option)}
          </MenuItem>
        ))}
      </Select>
    </SiteSelectBox>
  );
};

export default SiteSelector;
